"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiCreateDropPollRequest } from "@/generated/models/ApiCreateDropPollRequest";
import { CalendarIcon, PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useId, useRef, useState } from "react";
export interface CreateDropPollDraft {
  readonly options: readonly string[];
  readonly multichoice: boolean;
  readonly closingTime: string;
}
interface CreateDropPollProps {
  readonly draft: CreateDropPollDraft;
  readonly disabled: boolean;
  readonly validationError: string | null;
  readonly onChange: (draft: CreateDropPollDraft) => void;
  readonly onRemove: () => void;
}
const MIN_POLL_OPTIONS = 2;
const MAX_POLL_OPTIONS = 100;
const MAX_POLL_OPTION_LENGTH = 500;
const DEFAULT_POLL_DURATION_MS = 24 * 60 * 60 * 1000;
const padDatePart = (value: number): string => value.toString().padStart(2, "0");
const toDateTimeLocalValue = (timestamp: number): string => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = padDatePart(date.getMonth() + 1);
  const day = padDatePart(date.getDate());
  const hours = padDatePart(date.getHours());
  const minutes = padDatePart(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};
export const createDefaultDropPollDraft = (): CreateDropPollDraft => ({
  options: ["", ""],
  multichoice: false,
  closingTime: toDateTimeLocalValue(Date.now() + DEFAULT_POLL_DURATION_MS)
});
const getUniqueNormalizedOptions = (options: readonly string[]): Set<string> => new Set(options.map(option => option.trim().toLowerCase()));
export const validateCreateDropPollDraft = (draft: CreateDropPollDraft | null): {
  readonly request: ApiCreateDropPollRequest | null;
  readonly error: string | null;
} => {
  if (!draft) {
    return {
      request: null,
      error: null
    };
  }
  const options = draft.options.map(option => option.trim()).filter(option => option.length > 0);
  if (options.length < MIN_POLL_OPTIONS) {
    return {
      request: null,
      error: `Add at least ${MIN_POLL_OPTIONS} options.`
    };
  }
  if (options.some(option => option.length > MAX_POLL_OPTION_LENGTH)) {
    return {
      request: null,
      error: `Options can be up to ${MAX_POLL_OPTION_LENGTH} characters.`
    };
  }
  if (getUniqueNormalizedOptions(options).size !== options.length) {
    return {
      request: null,
      error: "Poll options must be unique."
    };
  }
  const closingTime = new Date(draft.closingTime).getTime();
  if (!Number.isFinite(closingTime) || closingTime <= Date.now()) {
    return {
      request: null,
      error: "Choose a future closing time."
    };
  }
  return {
    request: {
      options,
      multichoice: draft.multichoice,
      closing_time: closingTime
    } as unknown as ApiCreateDropPollRequest,
    error: null
  };
};
export default function CreateDropPoll(t0) {
  const $ = _c(110);
  const {
    draft,
    disabled,
    validationError,
    onChange,
    onRemove
  } = t0;
  const canAddOption = draft.options.length < MAX_POLL_OPTIONS;
  const canRemoveOption = draft.options.length > MIN_POLL_OPTIONS;
  const closingTimeInputId = useId();
  const optionKeyBaseId = useId();
  const closingTimeInputRef = useRef(null);
  const nextOptionKeyIndexRef = useRef(draft.options.length);
  let t1;
  if ($[0] !== draft.options || $[1] !== optionKeyBaseId) {
    t1 = () => draft.options.map((_, index) => `${optionKeyBaseId}-option-${index}`);
    $[0] = draft.options;
    $[1] = optionKeyBaseId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [optionKeys, setOptionKeys] = useState(t1);
  let t2;
  if ($[3] !== optionKeyBaseId) {
    t2 = () => {
      const key = `${optionKeyBaseId}-option-${nextOptionKeyIndexRef.current}`;
      nextOptionKeyIndexRef.current = nextOptionKeyIndexRef.current + 1;
      return key;
    };
    $[3] = optionKeyBaseId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const createOptionKey = t2;
  const [minClosingTime] = useState(_temp);
  let t3;
  if ($[5] !== draft || $[6] !== onChange) {
    t3 = (index_0, value) => {
      onChange({
        ...draft,
        options: draft.options.map((option, optionIndex) => optionIndex === index_0 ? value : option)
      });
    };
    $[5] = draft;
    $[6] = onChange;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const updateOption = t3;
  let t4;
  if ($[8] !== canRemoveOption || $[9] !== draft || $[10] !== onChange) {
    t4 = index_1 => {
      if (!canRemoveOption) {
        return;
      }
      setOptionKeys(current => current.filter((__0, optionIndex_0) => optionIndex_0 !== index_1));
      onChange({
        ...draft,
        options: draft.options.filter((__1, optionIndex_1) => optionIndex_1 !== index_1)
      });
    };
    $[8] = canRemoveOption;
    $[9] = draft;
    $[10] = onChange;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const removeOption = t4;
  let t5;
  if ($[12] !== canAddOption || $[13] !== createOptionKey || $[14] !== draft || $[15] !== onChange) {
    t5 = () => {
      if (!canAddOption) {
        return;
      }
      const nextOptionKey = createOptionKey();
      setOptionKeys(current_0 => [...current_0, nextOptionKey]);
      onChange({
        ...draft,
        options: [...draft.options, ""]
      });
    };
    $[12] = canAddOption;
    $[13] = createOptionKey;
    $[14] = draft;
    $[15] = onChange;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const addOption = t5;
  let t6;
  if ($[17] !== disabled) {
    t6 = () => {
      if (disabled) {
        return;
      }
      const input = closingTimeInputRef.current;
      if (!input) {
        return;
      }
      const pickerInput = input as HTMLInputElement & {
        readonly showPicker?: () => void;
      };
      if (typeof pickerInput.showPicker === "function") {
        try {
          pickerInput.showPicker();
        } catch {
          input.focus();
        }
        return;
      }
      input.focus();
    };
    $[17] = disabled;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const openClosingTimePicker = t6;
  let t10;
  let t11;
  let t7;
  let t8;
  let t9;
  if ($[19] !== canRemoveOption || $[20] !== disabled || $[21] !== draft || $[22] !== onChange || $[23] !== onRemove || $[24] !== optionKeyBaseId || $[25] !== optionKeys || $[26] !== removeOption || $[27] !== updateOption) {
    let t12;
    if ($[33] !== optionKeyBaseId || $[34] !== optionKeys) {
      t12 = (option_0, index_2) => ({
        index: index_2,
        key: optionKeys[index_2] ?? `${optionKeyBaseId}-option-${index_2}`,
        option: option_0
      });
      $[33] = optionKeyBaseId;
      $[34] = optionKeys;
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    const optionRows = draft.options.map(t12);
    t10 = "tw-mt-3 tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-900/80 tw-shadow-2xl tw-shadow-black/60 tw-backdrop-blur";
    let t13;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <h3 className="tw-mb-0 tw-text-[13.5px] tw-font-bold tw-tracking-wide tw-text-iron-50">Create Poll</h3>;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    const t14 = !draft.multichoice;
    let t15;
    if ($[37] !== draft || $[38] !== onChange) {
      t15 = () => onChange({
        ...draft,
        multichoice: false
      });
      $[37] = draft;
      $[38] = onChange;
      $[39] = t15;
    } else {
      t15 = $[39];
    }
    const t16 = `tw-rounded-md tw-border-0 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-transition-all tw-duration-200 disabled:tw-cursor-not-allowed ${draft.multichoice ? "tw-bg-iron-800/60 tw-text-iron-400 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-iron-200" : "tw-bg-iron-50 tw-text-iron-950 tw-shadow-sm"}`;
    let t17;
    if ($[40] !== disabled || $[41] !== t14 || $[42] !== t15 || $[43] !== t16) {
      t17 = <button type="button" aria-pressed={t14} disabled={disabled} onClick={t15} className={t16}>Single</button>;
      $[40] = disabled;
      $[41] = t14;
      $[42] = t15;
      $[43] = t16;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    let t18;
    if ($[45] !== draft || $[46] !== onChange) {
      t18 = () => onChange({
        ...draft,
        multichoice: true
      });
      $[45] = draft;
      $[46] = onChange;
      $[47] = t18;
    } else {
      t18 = $[47];
    }
    const t19 = `tw-rounded-md tw-border-0 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-transition-all tw-duration-200 disabled:tw-cursor-not-allowed ${draft.multichoice ? "tw-bg-iron-50 tw-text-iron-950 tw-shadow-sm" : "tw-bg-iron-800/60 tw-text-iron-400 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-iron-200"}`;
    let t20;
    if ($[48] !== disabled || $[49] !== draft.multichoice || $[50] !== t18 || $[51] !== t19) {
      t20 = <button type="button" aria-pressed={draft.multichoice} disabled={disabled} onClick={t18} className={t19}>Multiple</button>;
      $[48] = disabled;
      $[49] = draft.multichoice;
      $[50] = t18;
      $[51] = t19;
      $[52] = t20;
    } else {
      t20 = $[52];
    }
    let t21;
    if ($[53] !== t17 || $[54] !== t20) {
      t21 = <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">{t13}<div className="tw-flex tw-items-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800/60 tw-p-0.5">{t17}{t20}</div></div>;
      $[53] = t17;
      $[54] = t20;
      $[55] = t21;
    } else {
      t21 = $[55];
    }
    let t22;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <XMarkIcon className="tw-size-4" aria-hidden="true" />;
      $[56] = t22;
    } else {
      t22 = $[56];
    }
    let t23;
    if ($[57] !== disabled || $[58] !== onRemove) {
      t23 = <button type="button" aria-label="Remove poll" disabled={disabled} onClick={onRemove} className="tw-flex tw-size-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-white/[0.035] tw-p-0 tw-text-iron-300 tw-transition-all disabled:tw-cursor-not-allowed disabled:tw-opacity-50 desktop-hover:hover:tw-border-white/20 desktop-hover:hover:tw-bg-white/[0.06] desktop-hover:hover:tw-text-white">{t22}</button>;
      $[57] = disabled;
      $[58] = onRemove;
      $[59] = t23;
    } else {
      t23 = $[59];
    }
    if ($[60] !== t21 || $[61] !== t23) {
      t11 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-white/10 tw-px-4 tw-pb-3 tw-pt-4">{t21}{t23}</div>;
      $[60] = t21;
      $[61] = t23;
      $[62] = t11;
    } else {
      t11 = $[62];
    }
    t9 = "tw-flex tw-flex-col tw-gap-3 tw-px-4 tw-py-3";
    t7 = "tw-flex tw-flex-col tw-gap-2";
    let t24;
    if ($[63] !== canRemoveOption || $[64] !== disabled || $[65] !== draft.multichoice || $[66] !== removeOption || $[67] !== updateOption) {
      t24 = t25 => {
        const {
          index: index_3,
          key: key_0,
          option: option_1
        } = t25;
        const hasOptionValue = option_1.trim().length > 0;
        return <div key={key_0} className="tw-group/option tw-flex tw-items-center tw-gap-2"><span className={`tw-flex tw-size-[15px] tw-flex-shrink-0 tw-items-center tw-justify-center tw-border tw-border-solid tw-transition-all ${draft.multichoice ? "tw-rounded-[4px]" : "tw-rounded-full"} ${hasOptionValue ? "tw-border-iron-400 tw-bg-iron-800" : "tw-border-iron-700 tw-bg-iron-900/60"}`} aria-hidden="true"><span className={`tw-transition-all ${draft.multichoice ? "tw-size-[7px] tw-rounded-[2px]" : "tw-size-1.5 tw-rounded-full"} ${hasOptionValue ? "tw-bg-iron-300" : "tw-bg-transparent"}`} /></span><input type="text" value={option_1} disabled={disabled} maxLength={MAX_POLL_OPTION_LENGTH} aria-label={`Poll option ${index_3 + 1}`} onChange={event => updateOption(index_3, event.target.value)} className="tw-min-w-0 tw-flex-1 tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800/80 tw-px-3 tw-py-2.5 tw-text-[13px] tw-font-medium tw-text-iron-50 tw-outline-none tw-transition-all placeholder:tw-text-iron-500 hover:tw-border-iron-600 hover:tw-bg-iron-800 focus:tw-border-white/30 focus:tw-bg-iron-800 disabled:tw-cursor-not-allowed disabled:tw-opacity-60" placeholder={`Option ${index_3 + 1}`} /><button type="button" aria-label={`Remove option ${index_3 + 1}`} disabled={disabled || !canRemoveOption} onClick={() => removeOption(index_3)} className={`tw-flex tw-size-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-border-0 tw-bg-transparent tw-p-0 tw-text-iron-400 tw-transition-all disabled:tw-cursor-not-allowed desktop-hover:hover:tw-bg-white/[0.04] desktop-hover:hover:tw-text-rose-400 ${canRemoveOption && !disabled ? "tw-opacity-80 desktop-hover:hover:tw-opacity-100" : "tw-opacity-40"}`}><TrashIcon className="tw-size-4" aria-hidden="true" /></button></div>;
      };
      $[63] = canRemoveOption;
      $[64] = disabled;
      $[65] = draft.multichoice;
      $[66] = removeOption;
      $[67] = updateOption;
      $[68] = t24;
    } else {
      t24 = $[68];
    }
    t8 = optionRows.map(t24);
    $[19] = canRemoveOption;
    $[20] = disabled;
    $[21] = draft;
    $[22] = onChange;
    $[23] = onRemove;
    $[24] = optionKeyBaseId;
    $[25] = optionKeys;
    $[26] = removeOption;
    $[27] = updateOption;
    $[28] = t10;
    $[29] = t11;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t10 = $[28];
    t11 = $[29];
    t7 = $[30];
    t8 = $[31];
    t9 = $[32];
  }
  const t12 = disabled || !canAddOption;
  let t13;
  let t14;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="tw-flex tw-size-4 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-dashed tw-border-iron-600"><PlusIcon className="tw-size-2.5" aria-hidden="true" /></span>;
    t14 = <span className="tw-text-sm tw-font-medium">Add option</span>;
    $[69] = t13;
    $[70] = t14;
  } else {
    t13 = $[69];
    t14 = $[70];
  }
  let t15;
  if ($[71] !== addOption || $[72] !== t12) {
    t15 = <button type="button" disabled={t12} onClick={addOption} className="tw-flex tw-w-fit tw-flex-shrink-0 tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-p-0 tw-text-iron-500 tw-transition-colors disabled:tw-cursor-not-allowed disabled:tw-opacity-40 desktop-hover:hover:tw-text-iron-300">{t13}{t14}</button>;
    $[71] = addOption;
    $[72] = t12;
    $[73] = t15;
  } else {
    t15 = $[73];
  }
  let t16;
  if ($[74] !== validationError) {
    t16 = validationError && <p className="tw-mb-0 tw-min-w-0 tw-truncate tw-text-right tw-text-[11px] tw-font-medium tw-text-amber-200">{validationError}</p>;
    $[74] = validationError;
    $[75] = t16;
  } else {
    t16 = $[75];
  }
  let t17;
  if ($[76] !== t15 || $[77] !== t16) {
    t17 = <div className="tw-mt-1 tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-3">{t15}{t16}</div>;
    $[76] = t15;
    $[77] = t16;
    $[78] = t17;
  } else {
    t17 = $[78];
  }
  let t18;
  if ($[79] !== t17 || $[80] !== t7 || $[81] !== t8) {
    t18 = <div className={t7}>{t8}{t17}</div>;
    $[79] = t17;
    $[80] = t7;
    $[81] = t8;
    $[82] = t18;
  } else {
    t18 = $[82];
  }
  let t19;
  if ($[83] !== closingTimeInputId) {
    t19 = <label htmlFor={closingTimeInputId} className="tw-mb-0 tw-text-[11px] tw-font-medium tw-text-iron-400">Closing time</label>;
    $[83] = closingTimeInputId;
    $[84] = t19;
  } else {
    t19 = $[84];
  }
  let t20;
  if ($[85] !== draft || $[86] !== onChange) {
    t20 = event_0 => onChange({
      ...draft,
      closingTime: event_0.target.value
    });
    $[85] = draft;
    $[86] = onChange;
    $[87] = t20;
  } else {
    t20 = $[87];
  }
  let t21;
  if ($[88] !== closingTimeInputId || $[89] !== disabled || $[90] !== draft.closingTime || $[91] !== minClosingTime || $[92] !== openClosingTimePicker || $[93] !== t20) {
    t21 = <input id={closingTimeInputId} ref={closingTimeInputRef} type="datetime-local" min={minClosingTime} value={draft.closingTime} disabled={disabled} onClick={openClosingTimePicker} onChange={t20} className="tw-min-w-0 tw-flex-1 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-pr-8 tw-text-[13.5px] tw-font-medium tw-text-iron-100 tw-outline-none tw-transition-all [color-scheme:dark] disabled:tw-cursor-not-allowed disabled:tw-opacity-60 sm:tw-flex-none [&::-webkit-calendar-picker-indicator]:tw-opacity-0" />;
    $[88] = closingTimeInputId;
    $[89] = disabled;
    $[90] = draft.closingTime;
    $[91] = minClosingTime;
    $[92] = openClosingTimePicker;
    $[93] = t20;
    $[94] = t21;
  } else {
    t21 = $[94];
  }
  let t22;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <CalendarIcon className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 tw-size-4 -tw-translate-y-1/2 tw-text-iron-300 tw-transition-colors group-hover/closing-time:tw-text-iron-50" aria-hidden="true" />;
    $[95] = t22;
  } else {
    t22 = $[95];
  }
  let t23;
  if ($[96] !== closingTimeInputId || $[97] !== t21) {
    t23 = <label htmlFor={closingTimeInputId} className="tw-group/closing-time tw-relative tw-mb-0 tw-flex tw-min-h-10 tw-w-full tw-cursor-pointer tw-items-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800/80 tw-px-3.5 tw-py-2 tw-transition-all focus-within:tw-border-white/30 focus-within:tw-bg-iron-800 hover:tw-border-iron-600 hover:tw-bg-iron-800 sm:tw-w-auto">{t21}{t22}</label>;
    $[96] = closingTimeInputId;
    $[97] = t21;
    $[98] = t23;
  } else {
    t23 = $[98];
  }
  let t24;
  if ($[99] !== t19 || $[100] !== t23) {
    t24 = <div className="tw-flex tw-flex-col tw-gap-2 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-white/10 tw-pt-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"><div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1.5 sm:tw-flex-row sm:tw-items-center">{t19}{t23}</div></div>;
    $[99] = t19;
    $[100] = t23;
    $[101] = t24;
  } else {
    t24 = $[101];
  }
  let t25;
  if ($[102] !== t18 || $[103] !== t24 || $[104] !== t9) {
    t25 = <div className={t9}>{t18}{t24}</div>;
    $[102] = t18;
    $[103] = t24;
    $[104] = t9;
    $[105] = t25;
  } else {
    t25 = $[105];
  }
  let t26;
  if ($[106] !== t10 || $[107] !== t11 || $[108] !== t25) {
    t26 = <div className={t10}>{t11}{t25}</div>;
    $[106] = t10;
    $[107] = t11;
    $[108] = t25;
    $[109] = t26;
  } else {
    t26 = $[109];
  }
  return t26;
}
function _temp() {
  return toDateTimeLocalValue(Date.now() + 60000);
}